import { useQueries } from '@tanstack/react-query';

import { MappedInternalPropertyWithOffers, listMappedInternalProperties } from '~/services/AccommodationService';
import BedbankService from '~/services/BedbankService';

export interface PromotionOption {
  id: string;
  name: string;
  supplier?: string;
  periodFrom?: string;
  periodTo?: string;
}

const BEDBANK_SOURCE = 'bedbank';

export interface UseBundlePromotionOptionsResult {
  promotionsById: Map<string, PromotionOption>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Resolves LE-side promotions (from `bedbankProperty.promotions`) across the bundle's properties.
 *
 * Two-step fan-out:
 *   1. For each svc-accommodation property -> `listMappedInternalProperties` to find its bedbank-source mapping.
 *   2. For each bedbank property -> `BedbankService.getPropertyEdit` to read its `promotions[]` array.
 *
 * Promotions are keyed by their svc-bedbank UUID, which is what svc-accommodation's bundle
 * materialiser matches against (`rate.meta.bedbank.promotionId`). Mirrors the structure of
 * `useBundleRatePlanOptions` for the LPC equivalent.
 */
export function useBundlePromotionOptions(
  propertyIds: Array<string>,
  enabled: boolean,
): UseBundlePromotionOptionsResult {
  const gated = enabled && propertyIds.length > 0;

  const mapped = useQueries({
    queries: propertyIds.map((propertyId) => ({
      queryKey: ['bundle-lpp-mapped-internal-properties', propertyId],
      enabled: gated,
      staleTime: 5 * 60_000,
      retry: 1,
      queryFn: async (): Promise<Array<MappedInternalPropertyWithOffers>> => {
        const res = await listMappedInternalProperties({
          propertyId,
          verificationStatuses: [],
        });
        return res?.result ?? [];
      },
    })),
    combine: (results) => {
      const ids = new Set<string>();
      for (const r of results) {
        for (const entry of r.data ?? []) {
          if (entry.internalProperty?.source !== BEDBANK_SOURCE) continue;
          // `internalProperty.sourceId` is the svc-bedbank property UUID.
          const bedbankPropertyId = entry.internalProperty.sourceId;
          if (bedbankPropertyId) ids.add(bedbankPropertyId);
        }
      }
      return {
        bedbankPropertyIds: [...ids].sort(),
        isFetching: results.some((r) => r.isFetching),
        isError: results.some((r) => r.isError),
      };
    },
  });

  const promotions = useQueries({
    queries: mapped.bedbankPropertyIds.map((bedbankPropertyId) => ({
      queryKey: ['bundle-lpp-promotions-by-property', bedbankPropertyId],
      enabled: gated,
      staleTime: 60_000,
      retry: 1,
      queryFn: async () => {
        // `getPropertyEdit` uses the `presentPromotionEdit` shape: `promoId`, `promoName`,
        // `promoPeriodSchedule.{promoPeriodFrom,promoPeriodTo}` (NOT the same as the public
        // `presentPromotion` shape which uses `id`, `name`, `periodSchedule.{from,to}`).
        // Bug history: hook initially used the public shape and silently dropped all promos.
        const res = (await BedbankService.getPropertyEdit(bedbankPropertyId)) as {
          result?: {
            promotions?: Array<{
              promoId: string;
              promoName?: string;
              supplier?: string;
              promoPeriodSchedule?: { promoPeriodFrom?: string; promoPeriodTo?: string };
              deletedAt?: string;
            }>;
          };
        };
        return res?.result?.promotions ?? [];
      },
    })),
    combine: (results) => {
      // Use the date string in YYYY-MM-DD form so we compare promo period dates (also stored
      // as date-only strings) without timezone weirdness. UTC is fine here — bedbank promos
      // are configured with date-precision schedules, not timestamps.
      const today = new Date().toISOString().slice(0, 10);
      const byId = new Map<string, PromotionOption>();
      for (const r of results) {
        for (const p of r.data ?? []) {
          if (!p.promoId || byId.has(p.promoId)) continue;
          // Skip soft-deleted promotions.
          if (p.deletedAt) continue;
          // Skip expired promotions (period end before today). Promos with no end date are
          // open-ended and treated as active. Future-dated promos are kept — admins often
          // configure bundles ahead of a campaign launch.
          const periodTo = p.promoPeriodSchedule?.promoPeriodTo;
          if (periodTo && periodTo < today) continue;
          byId.set(p.promoId, {
            id: p.promoId,
            name: p.promoName ?? p.promoId,
            supplier: p.supplier,
            periodFrom: p.promoPeriodSchedule?.promoPeriodFrom,
            periodTo: p.promoPeriodSchedule?.promoPeriodTo,
          });
        }
      }
      return {
        promotionsById: byId,
        isFetching: results.some((r) => r.isFetching),
        isError: results.some((r) => r.isError),
      };
    },
  });

  return {
    promotionsById: promotions.promotionsById,
    isLoading: mapped.isFetching || promotions.isFetching,
    isError: mapped.isError || promotions.isError,
  };
}
